import React, { useEffect, useState, useRef, useContext, useMemo } from 'react'
import {
  StyleSheet,
  View,
  Text,
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
} from 'react-native'
import { FreshWaterContext } from '../../context/FreshWater/FreshWaterContext'
import { OptionsItem } from '../OptionsItem'
import { corporateColor } from '../../theme/colors'
import { Option } from '../../context/FreshWater/types'
import { AuthContext } from '../../context/Auth/AuthContext'
import { SeaWaterContext } from '../../context/SeaWater/SeaWaterContext'
import { useTranslation } from 'react-i18next'
import { DataMapTypeByParent } from '../../types'

interface Props {
  parentId: number
  onChange: (value: number) => void
  initialValue?: number
}

export const RoomsModule = ({ parentId, onChange, initialValue }: Props) => {
  const {
    user: { areaSelected },
  } = useContext(AuthContext)

  const { rooms } = useContext(FreshWaterContext)
  const { modules } = useContext(SeaWaterContext)

  const dataMap: DataMapTypeByParent = useMemo(
    () => ({
      sweetWater: rooms,
      seaWater: modules,
    }),
    [],
  )
  const { t } = useTranslation()
  const [data, setData] = useState<Option[]>([])
  const [selectedOption, setSelectedOption] = useState<number | null>(initialValue ?? null)
  const isFirstRender = useRef(true)

  const scrollPosition = useRef<number>(0)
  const flatListRef = useRef<FlatList<Option>>(null)

  useEffect(() => {
    onChange(selectedOption ?? 0)
  }, [selectedOption])

  useEffect(() => {
    const data = dataMap[areaSelected!]
    if (data) {
      setData(data[parentId] ?? [])
    } else {
      setData([])
    }
  }, [parentId])

  useEffect(() => {
    flatListRef.current?.scrollToOffset({ offset: scrollPosition.current, animated: false })

    if (!isFirstRender.current) {
      setSelectedOption(null)
    }
    isFirstRender.current = false
  }, [parentId])

  const handleOptionPress = (id: number) => {
    setSelectedOption(id === selectedOption ? null : id)
  }

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const offsetY = event.nativeEvent.contentOffset.y
    if (!isNaN(offsetY)) {
      scrollPosition.current = offsetY
    }
  }

  return (
    <View style={styles.container}>
      <Text style={styles.title}>
        {t(`Selecciona ${areaSelected === 'sweetWater' ? 'una sala' : 'un módulo'}`)}
      </Text>
      <FlatList
        ref={flatListRef}
        style={styles.flatList}
        data={data}
        renderItem={({ item }) => (
          <OptionsItem
            item={item}
            onPress={() => handleOptionPress(item.id)}
            isSelected={item.id === selectedOption}
          />
        )}
        onScroll={handleScroll}
        horizontal
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    marginTop: 20,
  },
  title: {
    textAlign: 'center',
    fontWeight: 'bold',
    color: corporateColor,
  },
  flatList: {
    marginTop: 10,
  },
})
